'use client';

import { useAuthContext } from '@/contexts/auth-context';
import { useAutoSavePreference } from '@/hooks/useAutoSavePreference';
import { Link } from '@/i18n/routing';
import { ShieldCheck, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

// ─────────────────────────────────────────────────────────────────
// ExistingUserPrivacyNotice — one-time informational notice for users
// who held an account BEFORE the privacy-by-default chat-upload change
// shipped (the existing `auto_save = true` cohort that was intentionally
// NOT back-filled).
//
// Legal basis: GDPR Art. 25 + Art. 5(1)(a) (fair/transparent processing,
// review-and-update duty), EDPB Guidelines 4/2019. It is PURELY
// INFORMATIONAL — it changes no user's setting; it only surfaces the new
// "Just this chat" option and points at the toggle. Copy approved (DRAFT)
// in docs/compliance/notice-existing-users-privacy-default.md.
//
// Only ever shown to signed-in (registered) users — see the
// `isAuthenticated` gate in `shouldShowPrivacyNotice` — since it targets the
// existing-account cohort, never anonymous visitors. Further scoped to the
// cohort the notice is actually FOR: users whose `auto_save_chat_uploads`
// is currently `true` (still auto-saving every chat upload to the library).
// Anyone already `false` — new users post-flip, or anyone who already chose
// per-chat control via AutoSavePrompt — gets no informational value from
// this banner and is excluded, minimizing impressions while still meeting
// the Art. 5(1)(a)/25 transparency duty for the affected cohort.
//
// ⚠️ ARMED (#6887, DPO directive 2026-06-30) — PRIVACY_DEFAULT_NOTICE_ENABLED
// is `true`, so this banner renders for eligible signed-in users once
// deployed. Human @citbaj ratification of the final copy is still pending
// (DPIA §2.2); flip the constant back to `false` if that ratification asks
// for a pause. The component also accepts an `enabled` prop purely to make
// the gate testable; production mounts it with no props.
// ─────────────────────────────────────────────────────────────────

/**
 * FEATURE FLAG — ARMED (true) per #6887 DPO directive 2026-06-30.
 *
 * While `true`, <ExistingUserPrivacyNotice /> renders the one-time notice to
 * signed-in users who haven't dismissed it. Set to `false` to pull the
 * banner (e.g. if @citbaj's pending copy ratification asks for changes).
 */
export const PRIVACY_DEFAULT_NOTICE_ENABLED = true;

/**
 * localStorage key persisting per-browser dismissal so the notice shows at
 * most once. (One-time only — "do not re-nag", per the delivery notes.)
 */
export const PRIVACY_DEFAULT_NOTICE_STORAGE_KEY =
  'existing_user_privacy_notice_dismissed';

/** Whether the one-time notice has already been dismissed in this browser. */
export function isPrivacyNoticeDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(PRIVACY_DEFAULT_NOTICE_STORAGE_KEY) === 'true';
}

/** Persist dismissal so the one-time notice never re-nags. */
export function markPrivacyNoticeDismissed(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PRIVACY_DEFAULT_NOTICE_STORAGE_KEY, 'true');
}

/**
 * Pure gating decision — the notice is shown only when the dark flag is on,
 * the visitor is a signed-in (registered) user — this notice targets the
 * pre-change existing-account cohort, never anonymous visitors — the user's
 * current `auto_save_chat_uploads` is `true` (the notice is only relevant to
 * users still on the old auto-save-everything behaviour), and it hasn't
 * already been dismissed. Extracted so the one-time logic is unit testable
 * without rendering the next-intl/Link tree (which is unreliable under the
 * suite's process-global next-intl mock).
 */
export function shouldShowPrivacyNotice(
  enabled: boolean,
  dismissed: boolean,
  isAuthenticated: boolean,
  autoSaveEnabled: boolean
): boolean {
  return enabled && isAuthenticated && autoSaveEnabled && !dismissed;
}

interface ExistingUserPrivacyNoticeProps {
  /**
   * Overrides the build-time {@link PRIVACY_DEFAULT_NOTICE_ENABLED} flag.
   * Exists for testability — production mounts the component with no props,
   * so the dark default-off flag is the single source of truth.
   */
  enabled?: boolean;
}

export default function ExistingUserPrivacyNotice({
  enabled = PRIVACY_DEFAULT_NOTICE_ENABLED,
}: ExistingUserPrivacyNoticeProps = {}) {
  const t = useTranslations('ExistingUserPrivacyNotice');
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthContext();
  const { autoSaveEnabled, isLoading: isAutoSaveLoading } =
    useAutoSavePreference();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Wait for auth AND the auto-save preference to resolve so logged-out
    // visitors never see this registered-user-only notice, and signed-in
    // users don't see it flash-then-hide before we know they're outside the
    // affected cohort (still auto-saving every upload).
    if (isAuthLoading || isAutoSaveLoading) return;
    // Persisted per-browser so the one-time notice doesn't re-nag.
    setIsVisible(
      shouldShowPrivacyNotice(
        enabled,
        isPrivacyNoticeDismissed(),
        isAuthenticated,
        autoSaveEnabled
      )
    );
  }, [
    enabled,
    isAuthenticated,
    isAuthLoading,
    autoSaveEnabled,
    isAutoSaveLoading,
  ]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    markPrivacyNoticeDismissed();
  }, []);

  if (!enabled || !isVisible) return null;

  return (
    // role='region' + a labelled landmark (not 'banner', which WAI-ARIA
    // reserves for the site-level header). Sticky so it participates in
    // document flow rather than covering the footer.
    <div
      role='region'
      aria-label={t('landmark')}
      className='sticky bottom-0 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300'
    >
      <div className='border-t border-line-default bg-surface-card px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-4 shadow-[0_-4px_16px_rgba(0,0,0,0.12)]'>
        <div className='mx-auto flex max-w-2xl flex-col gap-3 sm:flex-row sm:items-start'>
          <ShieldCheck
            className='hidden h-6 w-6 flex-shrink-0 text-status-info sm:block'
            aria-hidden='true'
          />

          <div className='min-w-0 flex-1'>
            <p className='text-sm font-semibold text-content-primary'>
              {t('title')}
            </p>
            <p className='mt-1 text-sm text-content-secondary'>{t('body')}</p>
            <p className='mt-1 text-sm text-content-secondary'>
              {t.rich('learnMore', {
                link: (chunks) => (
                  <Link
                    href='/privacy-policy'
                    className='rounded-sm text-brand-tertiary underline-offset-2 hover:text-brand-tertiary-hover hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary/50'
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </p>

            <div className='mt-3 flex flex-wrap items-center gap-2'>
              <button
                type='button'
                onClick={handleDismiss}
                className='inline-flex min-h-[44px] items-center justify-center rounded-lg bg-brand-tertiary px-4 py-2.5 text-sm font-medium text-content-inverse transition-colors hover:bg-brand-tertiary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary/50 focus-visible:ring-offset-2'
              >
                {t('gotIt')}
              </button>
              <Link
                href='/user-profile/preferences'
                onClick={handleDismiss}
                className='inline-flex min-h-[44px] items-center justify-center rounded-lg border border-line-default px-4 py-2.5 text-sm font-medium text-content-primary transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary/50 focus-visible:ring-offset-2'
              >
                {t('openSettings')}
              </Link>
            </div>
          </div>

          <button
            type='button'
            onClick={handleDismiss}
            className='flex-shrink-0 self-start rounded-lg p-3 text-content-muted transition-colors hover:bg-surface-muted hover:text-brand-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary/50'
            aria-label={t('dismiss')}
          >
            <X className='h-5 w-5' />
          </button>
        </div>
      </div>
    </div>
  );
}
